/* eslint-disable camelcase */
import { useMemo, useEffect, useState } from "react";

import {
  APICheckEligibilityResponse,
  APICheckEligibilityResponseError,
  APISignUpResponse,
  APISignUpResponseError,
  APIUpdateEmailResponse,
  APIUpdateEmailResponseError,
} from "@companion-core/shared/app/Interfaces/API";
import { useTranslation } from "react-i18next";
import { useRegisterStore } from "@companion-core/shared/app/Store/registerStore";
import { useTrackingStore } from "@companion-core/shared/app/Store/trackingStore";
import {
  useRegisterRequest,
  useRegisterUpdateEmail,
  useSendRegisterVerificationEmail,
  useSignUp,
} from "@companion-core/shared/app/Queries";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { dateInputToDatePayloadFormat } from "@companion-core/shared/app/Utils/datetime";
import { trackClickRegister } from "@companion-core/shared/app/tracking/register";
import { loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";

export const useRegisterContent = (registerState: string, lang: string) => {
  const { t } = useTranslation();
  const registerModule = getModule("register");
  const formPath = useMemo(() => registerModule?.formPath, [registerModule]);
  const jsonForm = useMemo(() => (formPath ? loadFormFromPath(formPath) : undefined), [formPath]);
  const noEmailFormPath = useMemo(() => jsonForm?.noEmailJsonPath, [jsonForm]);
  const noEmailJsonForm = useMemo(
    () => (noEmailFormPath ? loadFormFromPath(noEmailFormPath) : undefined),
    [noEmailFormPath],
  );
  const vcesEnabled = useMemo(() => registerModule?.vces?.enabled, [registerModule]);
  const {
    memberToRegister,
    setMemberToRegister,
    correlationId,
    setIsLoading,
    setRegisterContent,
    registerError,
    setRegisterError,
  } = useRegisterStore((state) => ({
    correlationId: state.correlationId,
    setMemberToRegister: state.setMemberToRegister,
    memberToRegister: state.memberToRegister,
    setIsLoading: state.setIsLoading,
    setRegisterContent: state.setRegisterContent,
    registerError: state.error,
    setRegisterError: state.setRegisterError,
  }));
  const { setCurrentPage } = useTrackingStore((state) => ({
    setCurrentPage: state.setCurrentPage,
  }));
  const datafeedMemberForm = useMemo(
    () => loadFormFromPath("register-forms/vces/datafeedMember"),
    [],
  );
  const vcesMemberForm = useMemo(() => loadFormFromPath("register-forms/vces/vcesMember"), []);
  const { mutateAsync: signUp } = useSignUp();
  const { mutateAsync: registerRequest } = useRegisterRequest();
  const { mutateAsync: registerEmailRequest } = useRegisterUpdateEmail();
  const { mutateAsync: sendRegisterVerificationEmail } = useSendRegisterVerificationEmail();
  const [obfuscatedEmail, setObfuscatedEmail] = useState<string | null>(null);

  const registerStepByState: Record<string, string> = useMemo(
    () => ({
      vces: "vces_card_number",
      "register-member-to-verify": "email_verification",
      "register-member-to-verify-confirmation": "email_verification_confirmation",
      "register-member-not-found": "err_info_perso",
      "register-member-already-verified": "err_info_perso",
      "register-error": "err_info_perso",
    }),
    [],
  );

  useEffect(() => {
    if (registerStepByState[registerState]) {
      setCurrentPage(registerStepByState[registerState]);
    }
  }, [registerState]);

  const onSubmitForm = async (payload: Record<string, unknown>) => {
    setIsLoading(true);
    const formattedPayload = {
      ...payload,
      language: lang,
    };
    try {
      const { data }: APICheckEligibilityResponse = await registerRequest({
        payload: formattedPayload,
        shouldEncryptData: true,
      });
      if (!data || !data?.result || !data?.result?.id) {
        setRegisterContent("register-error");
        return;
      }
      if (data.result.has_email) {
        setMemberToRegister({
          memberId: data.result.id,
        });
        setObfuscatedEmail(data.result.email ?? null);
        setRegisterContent("register-member-to-verify");
      } else {
        setMemberToRegister({
          memberId: data.result.id,
          "eligibility_criteria": payload,
        });
        setRegisterContent("register-no-email");
      }
    } catch (error: unknown) {
      const code = (error as APICheckEligibilityResponseError)?.response?.data.code;
      const message = (error as APICheckEligibilityResponseError)?.response?.data.error_message
        ?.replace(" ", "_")
        .toLowerCase();
      if (code === 404) {
        setRegisterError("member_not_found");
        setRegisterContent("register-member-not-found");
      } else if (code !== undefined && [408, 409].includes(code)) {
        setRegisterError("already_registered");
        setRegisterContent("register-member-already-verified");
      } else {
        setRegisterError(message ?? null);
        setRegisterContent("register-error");
      }
    }
    setIsLoading(false);
  };

  const onSubmitFormEmail = async (payload: Record<string, unknown>) => {
    setIsLoading(true);
    if (!memberToRegister) return setRegisterContent("register-error");
    const formattedPayload = {
      type: "personal",
      ...payload,
      ...memberToRegister.eligibility_criteria,
      language: lang,
    };
    try {
      const { data }: APIUpdateEmailResponse = await registerEmailRequest({
        payload: formattedPayload,
        memberId: memberToRegister?.memberId,
      });
      if (!data || !data?.result || !data?.result?.id) {
        setRegisterContent("register-error");
        return;
      }
      setMemberToRegister({ ...memberToRegister });
      setRegisterContent("register-member-to-verify");
    } catch (error: unknown) {
      const message = (error as APICheckEligibilityResponseError)?.response?.data.error_message
        ?.replace(" ", "_")
        .toLowerCase();
      if ((error as APIUpdateEmailResponseError)?.response?.data?.code === 404) {
        setRegisterError("member_not_found");
        setRegisterContent("register-member-not-found");
      } else {
        setRegisterError(message ?? null);
        setRegisterContent("register-error");
      }
    }
    setIsLoading(false);
  };

  const submitDatafeedMemberForm = async (payload: Record<string, unknown>) => {
    setIsLoading(true);
    const completePayload = {
      ...payload,
      correlation_id: correlationId,
      language: lang,
    };
    try {
      const { data }: APICheckEligibilityResponse = await registerRequest({
        payload: completePayload,
        shouldEncryptData: true,
      });
      if (!data || !data?.result) {
        // API response is not valid
        setRegisterContent("register-error");
        return;
      }
      if (!data.result.is_eligible || !data.result.has_email || !data.result.id) {
        // Member is not eligible
        setRegisterContent("register-error");
        return;
      }
      if (!data.result.is_email_verify) {
        // Member is eligible and has not been verified
        setMemberToRegister({
          memberId: data.result.id,
        });
        setRegisterContent("register-member-to-verify");
      } else {
        // Member has already been verified
        setRegisterContent("register-member-already-verified");
      }
    } catch (error: unknown) {
      const code = (error as APICheckEligibilityResponseError)?.response?.data.code;
      const message = (error as APICheckEligibilityResponseError)?.response?.data.error_message
        ?.replace(" ", "_")
        .toLowerCase();
      if (code === 404) {
        setRegisterError("member_not_found");
        setRegisterContent("register-member-not-found");
      } else if (code !== undefined && [408, 409].includes(code)) {
        setRegisterError("already_registered");
        setRegisterContent("register-member-already-verified");
      } else {
        setRegisterError(message ?? null);
        setRegisterContent("register-error");
      }
    }
    setIsLoading(false);
  };

  const submitVCESMemberForm = async (payload: Record<string, unknown>) => {
    setIsLoading(true);
    const completePayload = {
      ...payload,
      correlation_id: correlationId,
      birthdate: dateInputToDatePayloadFormat((payload.birthdate as string) ?? ""),
      preferred_language: lang,
    };
    try {
      const { data }: APISignUpResponse = await signUp(completePayload);
      if (!data?.result?.id) {
        // API response is not valid
        setRegisterContent("register-error");
        return;
      }
      setMemberToRegister({
        memberId: data.result.id,
      });
      setRegisterContent("register-member-to-verify");
    } catch (error: unknown) {
      const code = (error as APISignUpResponseError)?.response?.data?.code;
      if (code !== undefined && [408, 409].includes(code)) {
        setRegisterError("already_registered");
        setRegisterContent("register-member-already-verified");
      } else {
        setRegisterError("card_is_not_eligible");
        setRegisterContent("register-error");
      }
    }
    setIsLoading(false);
  };

  const onRetryToSendEmail = async () => {
    trackClickRegister("email_verification_retry");
    if (!memberToRegister) {
      setRegisterError("internal_error");
      setRegisterContent("register-error");
      return;
    }
    try {
      await sendRegisterVerificationEmail(memberToRegister.memberId);
      setRegisterContent("register-member-to-verify-confirmation");
    } catch (error: unknown) {
      const message = (error as APICheckEligibilityResponseError)?.response?.data.error_message
        ?.replace(" ", "_")
        .toLowerCase();
      setRegisterError(message ?? null);
      setRegisterContent("register-error");
    }
  };

  const dynamicFormName = useMemo(() => {
    const splitPath = formPath?.split("/");
    if (!splitPath) return "form";
    return splitPath[splitPath.length - 1].replace(".json", "");
  }, [formPath]);

  const dynamicFormNoEmailName = useMemo(() => {
    const splitPath = noEmailFormPath?.split("/");
    if (!splitPath) return "form";
    return splitPath[splitPath.length - 1].replace(".json", "");
  }, [noEmailFormPath]);

  return {
    t,
    datafeedMemberForm,
    vcesMemberForm,
    jsonForm,
    noEmailJsonForm,
    vcesEnabled,
    onSubmitForm,
    onSubmitFormEmail,
    submitDatafeedMemberForm,
    submitVCESMemberForm,
    onRetryToSendEmail,
    dynamicFormName,
    dynamicFormNoEmailName,
    registerError,
    setRegisterError,
    obfuscatedEmail,
  };
};
